import React, { useEffect, useMemo, useRef, useState } from "react"
import useProfile from "../../hooks/useProfile.js"
import HomepageScreenshot from "../shared/HomepageScreenshot.jsx"

const TONE_OPTIONS = [ "formal", "casual", "playful", "professional", "luxury" ]
const PALETTE_SLOTS = [ "primary", "accent", "background", "text", "cta" ]
const TYPOGRAPHY_FIELDS = [ "heading_family", "body_family", "heading_feel", "body_feel" ]
const PHASE_LABELS = [
  "phase_render", "phase_locale", "phase_ai", "phase_save",
  "phase_selectors", "phase_facts", "phase_screenshot", "phase_vision"
]

const ARCHETYPE_OPTIONS = [ "discount_warehouse", "lifestyle", "luxury", "technical", "marketplace", "niche" ]
const POSITIONING_OPTIONS = [ "cheapest", "value", "mainstream", "premium", "luxury" ]
const FACT_CATEGORIES = [ "shipping", "returns", "payments", "trust", "guarantees", "contact" ]
const COPY_SAMPLE_FIELDS = [ "hero_headline", "primary_cta", "promo_banner", "checkout_button" ]

const DEBOUNCE_MS = 400

function SectionHeader({ t, eyebrowKey, titleKey, ledeKey }) {
  return (
    <header className="space-y-2">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
      >
        {t(`dashboard.profile.${eyebrowKey}`)}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t(`dashboard.profile.${titleKey}`)}
      </h2>
      {ledeKey && (
        <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
          {t(`dashboard.profile.${ledeKey}`)}
        </p>
      )}
    </header>
  )
}

function SaveIndicator({ t, saving, savedAt, error }) {
  if (error) return <span className="font-mono text-[11px] text-failure">{error}</span>
  if (saving) return <span className="font-mono text-[11px] text-ink-tertiary">{t("dashboard.profile.saving")}</span>
  if (savedAt) return <span className="font-mono text-[11px] text-winner">{t("dashboard.profile.saved")}</span>
  return null
}

function OverrideBadge({ t, overridden, onRevert }) {
  if (!overridden) return null
  return (
    <button
      type="button"
      onClick={onRevert}
      className="font-mono text-[10px] text-ink-tertiary hover:text-ai"
      style={{ letterSpacing: "0.12em" }}
      title={t("dashboard.profile.revert_help")}
    >
      {t("dashboard.profile.manual_revert")}
    </button>
  )
}

function FieldRow({ label, children, right }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <label className="text-[13px] font-medium text-ink-secondary">{label}</label>
        {right}
      </div>
      {children}
    </div>
  )
}

function TextField({ value, onChange, onBlur, placeholder, multiline }) {
  if (multiline) {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        rows={3}
        className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
      />
    )
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
    />
  )
}

function ColorPicker({ value, onCommit, t }) {
  const hex = value || "#ffffff"
  const [draft, setDraft] = useState(hex)

  useEffect(() => { setDraft(hex) }, [ hex ])

  const commit = (next) => {
    const clean = /^#[0-9a-fA-F]{3,8}$/.test(next) ? next : draft
    setDraft(clean)
    if (clean !== value) onCommit(clean)
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={draft.length === 7 ? draft : "#000000"}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        className="w-10 h-10 rounded-card border border-rule cursor-pointer"
        aria-label={t("dashboard.profile.color_picker_aria")}
      />
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        className="w-28 font-mono text-[12px] bg-surface-card border border-rule rounded-card px-2 py-1 text-ink-primary"
        style={{ fontVariantNumeric: "tabular-nums" }}
      />
    </div>
  )
}

// --- Sections ---

function BrandSection({ t, profile, overridden, save, revert }) {
  const [ industry, setIndustry ] = useState(profile.industry || "")
  const [ brand_voice, setBrandVoice ] = useState(profile.brand_voice || "")

  useEffect(() => { setIndustry(profile.industry || "") }, [ profile.industry ])
  useEffect(() => { setBrandVoice(profile.brand_voice || "") }, [ profile.brand_voice ])

  return (
    <section className="space-y-5">
      <SectionHeader t={t} eyebrowKey="brand_eyebrow" titleKey="brand_title" ledeKey="brand_lede" />
      <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        <FieldRow
          label={t("dashboard.profile.industry_label")}
          right={<OverrideBadge t={t} overridden={overridden("industry")} onRevert={() => revert("industry")} />}
        >
          <TextField
            value={industry}
            onChange={setIndustry}
            onBlur={() => { if (industry !== profile.industry) save({ industry }) }}
          />
        </FieldRow>
        <FieldRow
          label={t("dashboard.profile.tone_label")}
          right={<OverrideBadge t={t} overridden={overridden("tone")} onRevert={() => revert("tone")} />}
        >
          <select
            value={profile.tone || ""}
            onChange={(e) => save({ tone: e.target.value })}
            className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
          >
            <option value="">—</option>
            {TONE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{t(`dashboard.profile.tone_${opt}`)}</option>
            ))}
          </select>
        </FieldRow>
      </div>
      <FieldRow
        label={t("dashboard.profile.brand_voice_label")}
        right={<OverrideBadge t={t} overridden={overridden("brand_voice")} onRevert={() => revert("brand_voice")} />}
      >
        <TextField
          value={brand_voice}
          onChange={setBrandVoice}
          onBlur={() => { if (brand_voice !== profile.brand_voice) save({ brand_voice }) }}
          multiline
        />
      </FieldRow>
    </section>
  )
}

function VisualIdentitySection({ t, profile, overridden, save, revert }) {
  const palette = profile.color_palette || {}
  const typo = profile.typography || {}

  // M1e: palette + typography are atomic overrides — vision AI writes them
  // as single hash units. Editing any slot flips the whole hash as overridden.
  const setSlot = (slot, hex) => {
    save({ color_palette: { ...palette, [slot]: hex } })
  }
  const setTypoField = (field, value) => {
    save({ typography: { ...typo, [field]: value } })
  }

  return (
    <section className="space-y-5">
      <SectionHeader t={t} eyebrowKey="visual_eyebrow" titleKey="visual_title" ledeKey="visual_lede" />

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.12em" }}>
            {t("dashboard.profile.palette_label")}
          </p>
          <OverrideBadge
            t={t}
            overridden={overridden("color_palette")}
            onRevert={() => revert("color_palette")}
          />
        </div>
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {PALETTE_SLOTS.map((slot) => (
            <FieldRow key={slot} label={t(`dashboard.profile.palette_${slot}`)}>
              <ColorPicker
                t={t}
                value={palette[slot]}
                onCommit={(hex) => { if (hex !== palette[slot]) setSlot(slot, hex) }}
              />
            </FieldRow>
          ))}
        </div>
      </div>

      <div className="pt-2">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.12em" }}>
            {t("dashboard.profile.typography_label")}
          </p>
          <OverrideBadge
            t={t}
            overridden={overridden("typography")}
            onRevert={() => revert("typography")}
          />
        </div>
        <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
          {TYPOGRAPHY_FIELDS.map((field) => (
            <FieldRow key={field} label={t(`dashboard.profile.typography_${field}`)}>
              <TextField
                value={typo[field]}
                onChange={(v) => setTypoField(field, v)}
                onBlur={() => { if (typo[field] !== (profile.typography || {})[field]) setTypoField(field, typo[field]) }}
              />
            </FieldRow>
          ))}
        </div>
      </div>

      {profile.visual_enriched_at && (
        <p className="font-mono text-[10px] text-ink-faint" style={{ letterSpacing: "0.12em" }}>
          {t("dashboard.profile.enriched_at", { at: new Date(profile.visual_enriched_at).toISOString() })}
        </p>
      )}
    </section>
  )
}

function ListEditor({ t, label, items, renderItem, renderEditor, onAdd, canAdd }) {
  return (
    <div className="space-y-3">
      <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.12em" }}>
        {label}
      </p>
      {items.length === 0 ? (
        <p className="text-[13px] text-ink-tertiary">{t("dashboard.profile.list_empty")}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((item, idx) => (
            <li key={idx}>{renderItem(item, idx)}</li>
          ))}
        </ul>
      )}
      <div className="pt-1">
        {renderEditor()}
      </div>
    </div>
  )
}

function CollectionSection({
  t, eyebrow, title, lede, overridden, onRevert, path,
  dirty, onSave, onRevertEdits, saving,
  children
}) {
  return (
    <section className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <SectionHeader t={t} eyebrowKey={eyebrow} titleKey={title} ledeKey={lede} />
        </div>
        <OverrideBadge t={t} overridden={overridden(path)} onRevert={() => onRevert(path)} />
      </div>
      {children}
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={onSave}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-40"
        >
          {saving ? t("dashboard.profile.saving") : t("dashboard.profile.save_changes")}
        </button>
        {dirty && (
          <button
            type="button"
            onClick={onRevertEdits}
            disabled={saving}
            className="text-[12px] text-ink-tertiary hover:text-ink-primary"
          >
            {t("dashboard.profile.discard")}
          </button>
        )}
      </div>
    </section>
  )
}

function ProductsSection({ t, profile, overridden, save, revert, saving }) {
  const [ draft, setDraft ] = useState(profile.products || [])
  const [ nameInput, setNameInput ] = useState("")
  const [ categoryInput, setCategoryInput ] = useState("")

  useEffect(() => { setDraft(profile.products || []) }, [ profile.products ])

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile.products || [])

  const add = () => {
    if (!nameInput.trim()) return
    setDraft([ ...draft, { name: nameInput.trim(), category: categoryInput.trim() } ])
    setNameInput(""); setCategoryInput("")
  }
  const remove = (idx) => setDraft(draft.filter((_, i) => i !== idx))

  return (
    <CollectionSection
      t={t}
      eyebrow="products_eyebrow"
      title="products_title"
      lede="products_lede"
      overridden={overridden}
      onRevert={revert}
      path="products"
      dirty={dirty}
      saving={saving}
      onSave={() => save({ products: draft })}
      onRevertEdits={() => setDraft(profile.products || [])}
    >
      <ul className="space-y-2">
        {draft.map((p, idx) => (
          <li key={idx} className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3">
            <div className="min-w-0">
              <span className="text-[13px] text-ink-primary">{p.name}</span>
              {p.category && (
                <span className="ml-2 font-mono text-[11px] text-ink-tertiary" style={{ letterSpacing: "0.1em" }}>
                  {p.category.toUpperCase()}
                </span>
              )}
            </div>
            <button type="button" onClick={() => remove(idx)} className="text-[12px] text-ink-tertiary hover:text-failure">
              {t("dashboard.profile.remove")}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2 pt-2">
        <input
          type="text"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          placeholder={t("dashboard.profile.product_name_placeholder")}
          className="flex-1 min-w-[160px] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        />
        <input
          type="text"
          value={categoryInput}
          onChange={(e) => setCategoryInput(e.target.value)}
          placeholder={t("dashboard.profile.product_category_placeholder")}
          className="flex-1 min-w-[160px] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        />
        <button
          type="button"
          onClick={add}
          disabled={!nameInput.trim()}
          className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-40"
        >
          {t("dashboard.profile.add")}
        </button>
      </div>
    </CollectionSection>
  )
}

function StringListSection({
  t, profile, overridden, save, revert, saving,
  field, eyebrow, title, lede, placeholder
}) {
  const [ draft, setDraft ] = useState(profile[field] || [])
  const [ input, setInput ] = useState("")

  useEffect(() => { setDraft(profile[field] || []) }, [ profile[field] ])

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile[field] || [])

  const add = () => {
    const v = input.trim()
    if (!v || draft.includes(v)) return
    setDraft([ ...draft, v ])
    setInput("")
  }
  const remove = (v) => setDraft(draft.filter((x) => x !== v))

  return (
    <CollectionSection
      t={t}
      eyebrow={eyebrow}
      title={title}
      lede={lede}
      overridden={overridden}
      onRevert={revert}
      path={field}
      dirty={dirty}
      saving={saving}
      onSave={() => save({ [field]: draft })}
      onRevertEdits={() => setDraft(profile[field] || [])}
    >
      <ul className="space-y-2">
        {draft.map((v) => (
          <li key={v} className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3">
            <code className="font-mono text-[12px] text-ink-primary" style={{ fontVariantNumeric: "tabular-nums" }}>{v}</code>
            <button type="button" onClick={() => remove(v)} className="text-[12px] text-ink-tertiary hover:text-failure">
              {t("dashboard.profile.remove")}
            </button>
          </li>
        ))}
      </ul>
      <form
        onSubmit={(e) => { e.preventDefault(); add() }}
        className="flex gap-2 pt-2"
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          className="flex-1 bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        />
        <button
          type="submit"
          disabled={!input.trim()}
          className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-40"
        >
          {t("dashboard.profile.add")}
        </button>
      </form>
    </CollectionSection>
  )
}

function TerminologySection({ t, profile, overridden, save, revert, saving }) {
  const [ draft, setDraft ] = useState(profile.terminology || {})
  const [ keyInput, setKeyInput ] = useState("")
  const [ valInput, setValInput ] = useState("")

  useEffect(() => { setDraft(profile.terminology || {}) }, [ profile.terminology ])

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile.terminology || {})

  const add = () => {
    const k = keyInput.trim(); const v = valInput.trim()
    if (!k || !v) return
    setDraft({ ...draft, [k]: v })
    setKeyInput(""); setValInput("")
  }
  const remove = (k) => {
    const next = { ...draft }; delete next[k]; setDraft(next)
  }

  return (
    <CollectionSection
      t={t}
      eyebrow="terminology_eyebrow"
      title="terminology_title"
      lede="terminology_lede"
      overridden={overridden}
      onRevert={revert}
      path="terminology"
      dirty={dirty}
      saving={saving}
      onSave={() => save({ terminology: draft })}
      onRevertEdits={() => setDraft(profile.terminology || {})}
    >
      <ul className="space-y-2">
        {Object.entries(draft).map(([ k, v ]) => (
          <li key={k} className="flex items-center justify-between gap-3 bg-surface-card border border-rule rounded-card px-4 py-3">
            <div className="min-w-0 flex-1">
              <span className="font-medium text-[13px] text-ink-primary">{k}</span>
              <span className="block text-[12px] text-ink-secondary mt-1">{v}</span>
            </div>
            <button type="button" onClick={() => remove(k)} className="text-[12px] text-ink-tertiary hover:text-failure">
              {t("dashboard.profile.remove")}
            </button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2 pt-2">
        <input
          type="text"
          value={keyInput}
          onChange={(e) => setKeyInput(e.target.value)}
          placeholder={t("dashboard.profile.terminology_key_placeholder")}
          className="flex-1 min-w-[160px] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        />
        <input
          type="text"
          value={valInput}
          onChange={(e) => setValInput(e.target.value)}
          placeholder={t("dashboard.profile.terminology_value_placeholder")}
          className="flex-1 min-w-[200px] bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        />
        <button
          type="button"
          onClick={add}
          disabled={!keyInput.trim() || !valInput.trim()}
          className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] font-medium text-ink-primary disabled:opacity-40"
        >
          {t("dashboard.profile.add")}
        </button>
      </div>
    </CollectionSection>
  )
}

function ShopIdentitySection({ t, profile, overridden, save, revert }) {
  return (
    <section className="space-y-5">
      <SectionHeader t={t} eyebrowKey="identity_eyebrow" titleKey="identity_title" ledeKey="identity_lede" />

      <HomepageScreenshot
        t={t}
        url={profile.homepage_screenshot_url}
      />

      <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        <FieldRow
          label={t("dashboard.profile.primary_language_label")}
          right={<OverrideBadge t={t} overridden={overridden("primary_language")} onRevert={() => revert("primary_language")} />}
        >
          <input
            type="text"
            value={profile.primary_language || ""}
            onChange={(e) => save({ primary_language: e.target.value })}
            placeholder="da-DK"
            className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary"
          />
        </FieldRow>
        <FieldRow
          label={t("dashboard.profile.currency_label")}
          right={<OverrideBadge t={t} overridden={overridden("currency")} onRevert={() => revert("currency")} />}
        >
          <input
            type="text"
            value={profile.currency || ""}
            onChange={(e) => save({ currency: e.target.value.toUpperCase() })}
            placeholder="DKK"
            className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary"
          />
        </FieldRow>
        <FieldRow
          label={t("dashboard.profile.money_format_label")}
          right={<OverrideBadge t={t} overridden={overridden("money_format_example")} onRevert={() => revert("money_format_example")} />}
        >
          <input
            type="text"
            value={profile.money_format_example || ""}
            onChange={(e) => save({ money_format_example: e.target.value })}
            placeholder="49,95 kr"
            className="w-full bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] font-mono text-ink-primary"
          />
        </FieldRow>
      </div>

      <FieldRow
        label={t("dashboard.profile.archetype_label")}
        right={<OverrideBadge t={t} overridden={overridden("design_archetype")} onRevert={() => revert("design_archetype")} />}
      >
        <select
          value={profile.design_archetype || ""}
          onChange={(e) => save({ design_archetype: e.target.value })}
          className="w-full max-w-md bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        >
          <option value="">—</option>
          {ARCHETYPE_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{t(`dashboard.profile.archetype_${opt}`)}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow
        label={t("dashboard.profile.positioning_label")}
        right={<OverrideBadge t={t} overridden={overridden("price_positioning")} onRevert={() => revert("price_positioning")} />}
      >
        <select
          value={profile.price_positioning || ""}
          onChange={(e) => save({ price_positioning: e.target.value })}
          className="w-full max-w-md bg-surface-card border border-rule rounded-card px-3 py-2 text-[13px] text-ink-primary"
        >
          <option value="">—</option>
          {POSITIONING_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>{t(`dashboard.profile.positioning_${opt}`)}</option>
          ))}
        </select>
      </FieldRow>

      <FieldRow
        label={t("dashboard.profile.persona_label")}
        right={<OverrideBadge t={t} overridden={overridden("audience_persona")} onRevert={() => revert("audience_persona")} />}
      >
        <TextField
          value={profile.audience_persona}
          onChange={(v) => save({ audience_persona: v })}
          onBlur={() => {}}
          multiline
        />
      </FieldRow>
    </section>
  )
}

function VoiceSamplesSection({ t, profile, overridden, save, revert }) {
  const samples = profile.copy_samples || {}

  const setField = (field, value) => {
    save({ copy_samples: { ...samples, [field]: value } })
  }

  return (
    <section className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <SectionHeader t={t} eyebrowKey="voice_eyebrow" titleKey="voice_title" ledeKey="voice_lede" />
        </div>
        <OverrideBadge t={t} overridden={overridden("copy_samples")} onRevert={() => revert("copy_samples")} />
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
        {COPY_SAMPLE_FIELDS.map((field) => (
          <FieldRow key={field} label={t(`dashboard.profile.copy_${field}`)}>
            <TextField
              value={samples[field] || ""}
              onChange={(v) => setField(field, v)}
              onBlur={() => {}}
              placeholder={t("dashboard.profile.copy_placeholder")}
            />
          </FieldRow>
        ))}
      </div>
    </section>
  )
}

function FactCategoryCard({ t, category, data, onRevert, overridden }) {
  const snippet = data?.verbatim_snippet
  const sourcePath = data?.source_page_path
  const leafEntries = Object.entries(data || {})
    .filter(([ k, v ]) => ![ "verbatim_snippet", "source_page_path" ].includes(k) && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0))

  if (leafEntries.length === 0) {
    return (
      <div className="bg-surface-card border border-rule rounded-card p-5">
        <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.12em" }}>
          {t(`dashboard.profile.facts_${category}`).toUpperCase()}
        </p>
        <p className="text-[13px] text-ink-tertiary mt-2" style={{ lineHeight: 1.55 }}>
          {t("dashboard.profile.facts_empty")}
        </p>
      </div>
    )
  }

  return (
    <div className="bg-surface-card border border-rule rounded-card p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="font-mono text-[10px] text-ink-tertiary" style={{ letterSpacing: "0.12em" }}>
          {t(`dashboard.profile.facts_${category}`).toUpperCase()}
        </p>
        <OverrideBadge t={t} overridden={overridden} onRevert={onRevert} />
      </div>
      <dl className="text-[13px] text-ink-primary space-y-1">
        {leafEntries.map(([ k, v ]) => (
          <div key={k} className="flex justify-between gap-3">
            <dt className="text-ink-secondary">{k}</dt>
            <dd className="font-mono" style={{ fontVariantNumeric: "tabular-nums" }}>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
          </div>
        ))}
      </dl>
      {snippet && (
        <blockquote className="text-[12px] text-ink-secondary italic border-l-2 border-ai pl-3" style={{ lineHeight: 1.55 }}>
          "{snippet}"
        </blockquote>
      )}
      {sourcePath && (
        <p className="font-mono text-[10px] text-ink-faint" style={{ letterSpacing: "0.12em" }}>
          SOURCE · {sourcePath}
        </p>
      )}
    </div>
  )
}

function VerifiedFactsSection({ t, profile, overridden, revert }) {
  const facts = profile.facts || {}
  return (
    <section className="space-y-5">
      <SectionHeader t={t} eyebrowKey="facts_eyebrow" titleKey="facts_title" ledeKey="facts_lede" />
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        {FACT_CATEGORIES.map((cat) => (
          <FactCategoryCard
            key={cat}
            t={t}
            category={cat}
            data={facts[cat]}
            overridden={overridden(`facts.${cat}`)}
            onRevert={() => revert(`facts.${cat}`)}
          />
        ))}
      </div>
    </section>
  )
}

function ReAnalyzeSection({ t, profile, onReAnalyze }) {
  const [ showConfirm, setShowConfirm ] = useState(false)
  const [ triggering, setTriggering ] = useState(false)
  const crawling = profile.crawl_status === "crawling"
  const current = profile.crawl_progress_current || 0
  const total = profile.crawl_progress_total || 4
  const phaseKey = PHASE_LABELS[Math.max(0, Math.min(PHASE_LABELS.length - 1, current))]

  const confirm = async () => {
    setTriggering(true)
    try { await onReAnalyze() } finally {
      setTriggering(false)
      setShowConfirm(false)
    }
  }

  return (
    <section className="space-y-5">
      <SectionHeader t={t} eyebrowKey="reanalyze_eyebrow" titleKey="reanalyze_title" ledeKey="reanalyze_lede" />

      {crawling ? (
        <div className="bg-surface-card border border-rule rounded-card p-5 space-y-3">
          <div className="flex items-center gap-3">
            <span className="inline-block w-[8px] h-[8px] rounded-full bg-ai animate-pulse" />
            <p
              className="font-mono text-[10px] text-ai"
              style={{ letterSpacing: "0.15em" }}
            >
              {t(`dashboard.profile.${phaseKey}`)}
            </p>
          </div>
          <div className="w-full h-1 bg-surface-subtle rounded-full overflow-hidden">
            <div
              className="h-full bg-ai transition-all"
              style={{ width: `${Math.round((current / total) * 100)}%` }}
            />
          </div>
          <p className="font-mono text-[11px] text-ink-tertiary" style={{ fontVariantNumeric: "tabular-nums" }}>
            {current} / {total}
          </p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          className="bg-brand text-ink-on-dark rounded-button px-5 py-2 text-[13px] font-medium"
        >
          {t("dashboard.profile.reanalyze_button")}
        </button>
      )}

      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowConfirm(false)}>
          <div className="bg-surface-card rounded-card-lg max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[18px] font-medium text-ink-primary" style={{ letterSpacing: "-0.025em" }}>
              {t("dashboard.profile.reanalyze_confirm_title")}
            </h3>
            <p className="text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
              {t("dashboard.profile.reanalyze_confirm_body")}
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="text-[13px] text-ink-tertiary hover:text-ink-primary"
              >
                {t("dashboard.profile.cancel")}
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={triggering}
                className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-40"
              >
                {triggering ? t("dashboard.profile.reanalyze_starting") : t("dashboard.profile.reanalyze_confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

// --- Main page ---

export default function ProfilePage({ t }) {
  const { profile, loading, save, revert, reAnalyze } = useProfile()
  const [ saving, setSaving ] = useState(false)
  const [ savedAt, setSavedAt ] = useState(null)
  const [ error, setError ] = useState(null)
  const saveTimerRef = useRef(null)

  useEffect(() => {
    if (!savedAt) return
    const id = setTimeout(() => setSavedAt(null), 2000)
    return () => clearTimeout(id)
  }, [ savedAt ])

  const overridden = useMemo(() => {
    const overrides = profile?.profile_overrides || {}
    return (path) => overrides[path] === true
  }, [ profile?.profile_overrides ])

  const doSave = async (patch) => {
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null }
    setSaving(true); setError(null)
    try {
      await save(patch)
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.profile.save_error"))
    } finally {
      setSaving(false)
    }
  }

  // Debounced save for rapidly-changing inputs (color pickers, number fields).
  const debouncedSave = (patch) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => doSave(patch), DEBOUNCE_MS)
  }

  const doRevert = async (path) => {
    setSaving(true); setError(null)
    try {
      await revert(path)
      setSavedAt(Date.now())
    } catch (err) {
      setError(err.message || t("dashboard.profile.save_error"))
    } finally {
      setSaving(false)
    }
  }

  if (loading || !profile) {
    return <p className="text-sm text-ink-tertiary">…</p>
  }

  return (
    <div className="space-y-12">
      <header className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="space-y-3">
          <p
            className="font-mono text-[10px] text-ai"
            style={{ letterSpacing: "0.15em", fontVariantNumeric: "tabular-nums" }}
          >
            {t("dashboard.profile.eyebrow")}
          </p>
          <h1
            className="text-[28px] font-medium text-ink-primary"
            style={{ letterSpacing: "-0.035em" }}
          >
            {t("dashboard.profile.title")}
          </h1>
          <p className="text-sm text-ink-secondary max-w-2xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.profile.lede")}
          </p>
        </div>
        <SaveIndicator t={t} saving={saving} savedAt={savedAt} error={error} />
      </header>

      <BrandSection t={t} profile={profile} overridden={overridden} save={doSave} revert={doRevert} />
      <VisualIdentitySection
        t={t}
        profile={profile}
        overridden={overridden}
        save={debouncedSave}
        revert={doRevert}
      />
      <ShopIdentitySection
        t={t}
        profile={profile}
        overridden={overridden}
        save={doSave}
        revert={doRevert}
      />
      <VoiceSamplesSection
        t={t}
        profile={profile}
        overridden={overridden}
        save={debouncedSave}
        revert={doRevert}
      />
      <VerifiedFactsSection
        t={t}
        profile={profile}
        overridden={overridden}
        revert={doRevert}
      />
      <ProductsSection t={t} profile={profile} overridden={overridden} save={doSave} revert={doRevert} saving={saving} />
      <StringListSection
        t={t}
        profile={profile}
        overridden={overridden}
        save={doSave}
        revert={doRevert}
        saving={saving}
        field="marketing_points"
        eyebrow="marketing_eyebrow"
        title="marketing_title"
        lede="marketing_lede"
        placeholder={t("dashboard.profile.marketing_placeholder")}
      />
      <TerminologySection t={t} profile={profile} overridden={overridden} save={doSave} revert={doRevert} saving={saving} />
      <StringListSection
        t={t}
        profile={profile}
        overridden={overridden}
        save={doSave}
        revert={doRevert}
        saving={saving}
        field="cta_selectors"
        eyebrow="cta_eyebrow"
        title="cta_title"
        lede="cta_lede"
        placeholder={t("dashboard.profile.cta_placeholder")}
      />
      <StringListSection
        t={t}
        profile={profile}
        overridden={overridden}
        save={doSave}
        revert={doRevert}
        saving={saving}
        field="checkout_selectors"
        eyebrow="checkout_eyebrow"
        title="checkout_title"
        lede="checkout_lede"
        placeholder={t("dashboard.profile.checkout_placeholder")}
      />
      <ReAnalyzeSection t={t} profile={profile} onReAnalyze={reAnalyze} />
    </div>
  )
}
